import React, {createContext, useContext, useMemo, useState} from 'react'
import {registerGlobalActions} from './global-actions'
import {uploadState} from './upload-state'
import {backdropState} from './backdrop-state'
import {highlightState} from './highlight-state'
import {toolbarsState} from './toolbars-state'
import {tracksState} from './tracks-state'
import {gridMediaState} from './grid-media-state'
import {contentStyleState} from './content-style-state'
import {lastTabState} from './last-tab-state'
import {contentSummaryState} from './content-summary-state'
import {albumEngagementState} from './album-engagement-state'
import {homePageState} from './home-page-state'
import {autoscrollState} from './autoscroll-state'
import {editModeState} from './edit-mode-state'
import {autoplayState} from './autoplay-state'
import {routeParamsState} from './route-params-state'
import {userMilestonesState} from './user-milestones-state'
import {helpSystemState} from './help-system-state'
import {profileStatsState} from './profile-stats-state'
import {videoPlaybackState} from './video-playback-state'
import {sessionState} from './session-state'
import {config} from '../config'
const SharedStoreContext = createContext({})
const DEFAULT_PRIVACY = {
    likeCount: true,
    commentCount: true,
    saveCount: true,
    shareCount: true,
}

export const $actions = {}
export const getUserScopedDefaultState = () => ({
    ...uploadState.defaultState,
    ...contentSummaryState.defaultState,
    ...albumEngagementState.defaultState,
    ...gridMediaState.defaultState,
    ...backdropState.defaultState,
    ...editModeState.defaultState,
    ...routeParamsState.defaultState,
    ...userMilestonesState.defaultState,
    userMilestones: {...userMilestonesState.defaultState.userMilestones},
    ...helpSystemState.defaultState,
    helpSystem: {...helpSystemState.defaultState.helpSystem},
    ...profileStatsState.defaultState,
    profileStatsChanges: {},
    privateMode: false,
    portfolioMode: false,
    notificationSettings: null,
    notificationsCheckedAt: null,
    followersSeenAt: null,
    messagesSeenAt: null,
    notificationSummaryRefreshAt: null,
    notifBadgeSummary: null,
    privacy: {...DEFAULT_PRIVACY},
})
export const resetUserScopedState = (state, nextUser) =>
    state.sessionUser?.id === nextUser?.id ? state : {...state, ...getUserScopedDefaultState()}
export const registerActions = actions => Object.keys(actions).forEach(actionName => {
    const action = actions[actionName]
    if (config.debug.sharedStore) console.warn('register-action', actionName, action)
    $actions[actionName] = action })
export const createSharedStoreActions = (setSharedState) => Object.keys($actions).reduce((acc, actionName) => ({
    ...acc, [actionName]: payload => {
        if (config.debug.sharedStore) console.warn('action-call', actionName, payload)
        setSharedState(state => {
            const newState = $actions[actionName](payload, state, setSharedState)
            if (config.debug.sharedStore) console.warn('new-state', newState)
            return newState
        })
    }
}), {})

registerActions({
    ...uploadState.actions,
    ...backdropState.actions,
    ...highlightState.actions,
    ...toolbarsState.actions,
    ...tracksState.actions,
    ...gridMediaState.actions,
    ...contentStyleState.actions,
    ...lastTabState.actions,
    ...contentSummaryState.actions,
    ...albumEngagementState.actions,
    ...homePageState.actions,
    ...autoscrollState.actions,
    ...editModeState.actions,
    ...autoplayState.actions,
    ...routeParamsState.actions,
    ...userMilestonesState.actions,
    ...helpSystemState.actions,
    ...profileStatsState.actions,
    ...videoPlaybackState.actions,
    ...sessionState.actions,
    setSessionUser: (user, state) => sessionState.actions.setSessionUser(user, resetUserScopedState(state, user)),
    setSessionError: (_, state) => sessionState.actions.setSessionError(null, resetUserScopedState(state, null)),
    setPortfolioMode: (value, state) => ({...state, portfolioMode: value}),
    setScrolledPast320: (value, state) => ({...state, scrolledPast320: value}),
    setNavbarScrollSuppressedUntil: (value, state) => ({...state, navbarScrollSuppressedUntil: value}),
    setNotificationSettings: (settings, state) => ({...state, notificationSettings: settings}),
    setNotificationsCheckedAt: (value, state) => ({...state, notificationsCheckedAt: value}),
    setFollowersSeenAt: (value, state) => ({...state, followersSeenAt: value}),
    setMessagesSeenAt: (value, state) => ({...state, messagesSeenAt: value}),
    setNotificationSummaryRefreshAt: (value, state) => ({...state, notificationSummaryRefreshAt: value}),
    setNotifBadgeSummary: (summary, state) => ({...state, notifBadgeSummary: summary}),
    setPublicConfig: (config, state) => ({...state, publicConfig: config}),
    setPublicConfigError: (error, state) => ({...state, publicConfigError: error}),
})

export const SharedStoreProvider = ({ children }) => {
    const [sharedState, setSharedState] = useState(() => {
        const path = window.location.pathname
        return {
            ...tracksState.defaultState,
            ...routeParamsState.defaultState,
            ...autoscrollState.defaultState,
            ...editModeState.defaultState,
            ...backdropState.defaultState,
            ...autoplayState.defaultState,
            ...uploadState.defaultState,
            ...toolbarsState.defaultState(path),
            ...contentStyleState.defaultState,
            ...homePageState.defaultState(path),
            ...highlightState.defaultState(path),
            ...contentSummaryState.defaultState,
            ...gridMediaState.defaultState,
            ...userMilestonesState.defaultState,
            ...helpSystemState.defaultState,
            ...profileStatsState.defaultState,
            ...videoPlaybackState.defaultState,
            ...sessionState.defaultState,
            adjustMode: true,
            scrolledPast320: false,
            navbarScrollSuppressedUntil: 0,
            publicConfig: null,
            publicConfigError: null,
            ...getUserScopedDefaultState(),
        }
    })

    const actions = useMemo(() => createSharedStoreActions(setSharedState), [setSharedState])
    registerGlobalActions(actions)

    return <SharedStoreContext.Provider value={{ sharedState, setSharedState, actions }}>
        {children}</SharedStoreContext.Provider>
};

export const useSharedStore = () => useContext(SharedStoreContext)
